import { Controller } from '@hotwired/stimulus';

type ItemElement = HTMLLIElement;

export default class extends Controller {
  static readonly targets = ['toggle', 'menuItem', 'menu'];

  declare toggleTarget: HTMLButtonElement;
  declare menuTarget: HTMLButtonElement;
  declare menuItemTargets: ItemElement[];

  private isMenuOpen = false;
  private currentMenuItem: ItemElement | undefined;

  initialize(): void {
    this.focusOnSelectedMenuItem();
  }

  connect(): void {
    this.handleMenuKeypress();
    this.handleFocusOnKeyPressed();
    this.handleItemOnBlur();
    this.onFocusOut();
  }

  toggle() {
    this.isMenuOpen = !this.isMenuOpen;
    this.toggleTarget.setAttribute('aria-expanded', String(this.isMenuOpen));
    if (this.isMenuOpen) {
      this.focusOnSelectedMenuItem();
    } else {
      this.toggleTarget.focus();
    }
  }

  close() {
    this.isMenuOpen = false;
    this.toggleTarget.setAttribute('aria-expanded', 'false');
    this.toggleTarget.focus();
  }

  private onFocusOut() {
    this.element.addEventListener('focusout', (event: Event) => {
      const node = (event as MouseEvent).relatedTarget as Node;
      if (this.isMenuOpen && !this.element.contains(node)) {
        this.toggle();
      }
    });
  }

  private handleMenuKeypress() {
    for (const [index, menuItem] of this.menuItemTargets.entries()) {
      menuItem.addEventListener('keydown', (event: KeyboardEvent) => {
        switch (event.key) {
          case 'ArrowUp':
          case 'Up': {
            this.moveToPreviousMenuItem(index);
            this.focusMenuItem();
            event.preventDefault();
            break;
          }
          case 'ArrowDown':
          case 'Down': {
            this.moveToNextMenuItem(index);
            this.focusMenuItem();
            event.preventDefault();
            break;
          }
          default: {
            break;
          }
        }
      });
    }
  }

  private handleItemOnBlur() {
    for (const menuItem of this.menuItemTargets) {
      menuItem.addEventListener('blur', (event) => {
        if (this.isMenuOpen) {
          (event.target as ItemElement).setAttribute('tabindex', '-1');
        }
      });
    }
  }

  private handleFocusOnKeyPressed() {
    this.toggleTarget.addEventListener('keydown', (event: KeyboardEvent) => {
      (event.target as ItemElement).focus();
    });
  }

  private moveToNextMenuItem(menuItemIndex: number) {
    this.currentMenuItem =
      menuItemIndex === this.menuItemTargets.length - 1
        ? this.menuItemTargets[0]
        : this.menuItemTargets[menuItemIndex + 1];
  }

  private moveToPreviousMenuItem(menuItemIndex: number) {
    this.currentMenuItem =
      menuItemIndex === 0
        ? (this.menuItemTargets.at(-1) as ItemElement)
        : this.menuItemTargets[menuItemIndex - 1];
  }

  private focusOnSelectedMenuItem() {
    let newFocusElement: ItemElement | undefined;
    for (const menuItem of this.menuItemTargets) {
      if (menuItem.ariaChecked === 'true') {
        newFocusElement = menuItem;
        break;
      }
    }
    this.currentMenuItem = newFocusElement ?? this.menuItemTargets[0];
    this.focusMenuItem();
  }

  private focusMenuItem() {
    this.currentMenuItem?.focus();
    this.currentMenuItem?.setAttribute('tabindex', '0');
  }
}
